import {
  Text,
  Flex,
  Stack,
  useColorModeValue,
  Icon,
  Badge,
  Box,
  Avatar,
  Image,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useEffect, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import FlipCard from "../../components/FlipCard.js";
import { FaGift, FaStar, FaChevronRight } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapLocationDot } from "@fortawesome/free-solid-svg-icons"; 
import RewardProgress from "../../components/UserComponents/RewardProgress.js";
import {
  useGetUserRewardQuery,
  useLogoutMutation,
  useGetUserDataMutation,
  useGetStampRewardDataMutation,
  useFetchUserStampTransactionsMutation,
  useCheckAndExpiredStampsTransactionsMutation,
  useCheckAndExpiredPointsTransactionsMutation
} from "../../slices/userApiSlice.js";
import { logout } from "../../slices/authSlice.js";
import UserMenu from "../../components/UserComponents/UserMenu.js";
import Hero from "../../components/UserComponents/Hero";
import UserHeader from "../../components/UserComponents/UserHeader.js";
// import NotificationBell from "../../components/UserComponents/NotificationBell.js";
// import NotificationDetailModal from "../../components/UserComponents/NotificationDetailModal.js";
import { IconButton, Spinner } from "@chakra-ui/react";
import StampReward from "../../components/UserComponents/StampReward.js";
import StampCard from "../../components/UserComponents/StampCard.js";
import dateUtils from "../../utils/formatDate.js";
import { useTranslation } from "react-i18next";

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get userInfo from Redux store
  const { userInfo } = useSelector((state) => state.auth);
  const [getUserData] = useGetUserDataMutation();
  const [userData , setUserData] = useState(null);

  const [logoutApiCall] = useLogoutMutation();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch rewards only if userInfo exists
  const { data: rewards, refetch } = useGetUserRewardQuery(userInfo?._id, {
    skip: !userInfo?._id, 
  });
  const [getStampReward, { isLoading, isError }] = useGetStampRewardDataMutation();
  const [stampRewardData, setStampRewardData] = useState();

  const [fetchUserStampTransactions, {isUserStampTransactionLoading}] = useFetchUserStampTransactionsMutation();
  const [updateStampExpiredTransactions] = useCheckAndExpiredStampsTransactionsMutation();
  const [updatePointExpiredTransactions] = useCheckAndExpiredPointsTransactionsMutation();

  // Chakra UI Color Mode
  const bgColor = useColorModeValue("teal.600", "gray.800");
  const bgColorDark = useColorModeValue("gray.50", "gray.700");
  const textColor = useColorModeValue("custom.800", "custom.900");
  const welcomeTextColor = useColorModeValue("custom.900", "teal.300");
  const memberDateTextColor = useColorModeValue("gray.400", "custom.900");
  const pointBadgeTextColor = useColorModeValue("teal.600", "teal.300");
  const rewardProgressTextColor = useColorModeValue("gray.600", "gray.300");
  const bgColorIcon = useColorModeValue('rgba(0, 0, 0, 0.2)', 'rgba(255, 255, 255, 0.2)');

  const [stamps, setStamps] = useState(0);
  const { t } = useTranslation();
  const savedLanguage = localStorage.getItem("i18nextLng") || "en";
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  // const [isModalOpen, setIsModalOpen] = useState(false);
  // const [selectedNotification, setSelectedNotification] = useState(null);

  // Dummy notifications data
  // const dummyNotifications = [
  //   {
  //     _id: '1',
  //     title: 'New Message',
  //     message: 'You have received a new message from John Doe',
  //     date: '2023-05-15T10:30:00Z',
  //     isRead: false,
  //     type: 'message'
  //   },
  //   {
  //     _id: '2',
  //     title: 'System Update',
  //     message: 'A new system update is available. Please update your app.',
  //     date: '2023-05-14T09:15:00Z',
  //     isRead: true,
  //     type: 'system'
  //   },
  //   {
  //     _id: '3',
  //     title: 'Reminder',
  //     message: 'Your meeting starts in 15 minutes',
  //     date: '2023-05-13T14:45:00Z',
  //     isRead: false,
  //     type: 'reminder'
  //   }
  // ];

  // const handleOpenModal = (notificationId) => {
  //   const notification = dummyNotifications.find(n => n._id === notificationId);
  //   setSelectedNotification(notification);
  //   setIsModalOpen(true);
  // };

  // const handleCloseModal = () => {
  //   setIsModalOpen(false);
  //   setSelectedNotification(null);
  // };

  // const handleMarkAsRead = (notificationId) => {
  //   console.log(`Marking notification ${notificationId} as read`);
  //   handleCloseModal();
  // };

  const [stampData, setStampData] = useState({
    earned: [],
    used: [],
    expired: []
  });

  const handleStampReward = useCallback(async () => {
    try {
        // Redeem logic
        const response = await getStampReward(userInfo?._id).unwrap();
        setStampRewardData(response);
    } catch (error) {
        console.error("Error redeeming stamp reward:", error);
    }
  }, [getStampReward, userInfo?._id]);

  const handleLogout = useCallback(async () => {
    try {
      // Check if the user is online
      // if (!navigator.onLine) {
      //   throw new Error("You are offline. Please check your internet connection.");
      // }

      await logoutApiCall().unwrap();
      
    } catch (err) {
      // const errorMessage =
      //   err.message === "Failed to fetch"
      //     ? "Network error. Please check your internet connection and try again."
      //     : "Something went wrong. Please try again.";
      //console.error(errorMessage);
    } finally {
      // Always log out locally, even if the API call fails
      dispatch(logout());
      navigate("/login");
    }
  }, [logoutApiCall, dispatch, navigate]);

  const refreshData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const userResponse = await getUserData(userInfo?._id).unwrap();
      setUserData(userResponse.user);
      
      await refetch();
    } catch (err) {
      //console.error("Failed to refresh data:", err);

      // Check if error is authentication-related
      if (err?.status === 401 || err?.status === 403) {
        handleLogout();  // Logout only if token is missing or invalid
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [getUserData, userInfo?._id, refetch, handleLogout]);

  useEffect(() => {
    if (userInfo) {
      const fetchData = async () => {
        try {
          refreshData(); // assuming it's not async
  
          await updatePointExpiredTransactions(userInfo._id).unwrap();
          await updateStampExpiredTransactions(userInfo._id).unwrap();
  
          setStamps(userData?.current_stamps);
        } catch (error) {
          console.error("Error updating expired transactions:", error);
        }
      };
  
      fetchData();
    }
  }, [
    userInfo, 
    refreshData, 
    userData?.current_stamps, 
    updatePointExpiredTransactions, 
    updateStampExpiredTransactions
  ]);  

  useEffect(() => {
    if (isOpen) {
        handleStampReward();
    }
  }, [isOpen, handleStampReward]);

  const handleFetchUserStampTransactions = async () => {
    try {
      const [responseFetchUserStamps] = await Promise.all([
        fetchUserStampTransactions(userData?._id).unwrap(),
        updateStampExpiredTransactions(userData?._id).unwrap()
      ]);
  
      // Ensure empty arrays if no data
      setStampData({
        earned: responseFetchUserStamps?.transactions?.earned || [],
        used: responseFetchUserStamps?.transactions?.used || [],
        expired: responseFetchUserStamps?.transactions?.expired || []
      });
  
    } catch (error) {
      //console.error("Error fetching stamp transactions:", error);
      setStampData({ earned: [], used: [], expired: [] }); // Reset to empty in case of failure
    }
  };
  
  return (
    <>
      {userInfo || userData ? (
        <>
          {/* User Info Section */}
          <Box 
            bg={bgColor}
            pt={`calc(1rem + env(safe-area-inset-top))`}
            >
            <Flex
              direction="row" // Align UserMenu and User Info in the same row
              align="center" // Center items vertically
              justify="center" // Ensure space between UserMenu and User Info
              bg={bgColor}
              color={textColor}
              pt="4"
              pb="85px"
              px="6"
              borderBottomRightRadius="2xl"
              borderBottomLeftRadius="2xl"
              position="relative"
            >
              {/* User Menu positioned to the left */}
              <Box position="absolute" top='5' left="0">
                <UserMenu userInfo={userData} logOutHandler={handleLogout} />
                
              </Box>
              {/* <Box position="absolute" top='3' right="5">
                <NotificationBell 
                  userId={userInfo?._id} 
                  notifications={dummyNotifications}
                  onNotificationClick={handleOpenModal}
                />
                <NotificationDetailModal
                  isOpen={isModalOpen}
                  onClose={handleCloseModal}
                  notification={selectedNotification}
                  onMarkAsRead={handleMarkAsRead}
                />
              </Box> */}

              {/* User Info */}
              <Flex align="center" gap={4} mb={6}> {/* Row alignment and spacing */}
                {/* Skeleton for Avatar */}
                <SkeletonCircle size="12" isLoaded={!isRefreshing}>
                  <Avatar size="md" src={userData?.image_url} />
                </SkeletonCircle>

                {/* Skeleton for Text */}
                <Box>
                  <SkeletonText
                    noOfLines={2}
                    spacing="2"
                    skeletonHeight="4"
                    isLoaded={!isRefreshing}
                  >
                    <Text color={welcomeTextColor} fontSize="lg">
                      {t("hi")}, <b>{userInfo?.name || "Member"}</b>
                    </Text>
                    <Text fontSize="xs" mt={1} color={memberDateTextColor}>
                      {t("member_since")}{" "}
                      {userInfo?.createdAt
                        ? dateUtils.formatDate(userInfo.createdAt, savedLanguage)
                        : "N/A"}
                    </Text>
                  </SkeletonText>
                </Box>
              </Flex>
            </Flex>
          </Box>
  
          {/* Main Content Section */}
          <Flex flex="1" align="center" bg={bgColorDark} justify="center" width="100%">
            <Stack spacing={0} w="full" maxW="lg" minH="70vh" bg={bgColorDark} p={3} my={5}>
              {/* Points Section */}
              <Box position="relative" borderWidth="1px" height="160px" display="inline-block" boxShadow="xl" p={3} bg={bgColorDark} rounded="xl" mt={-28}>
                <Flex align="center" gap={2}>
                  {/* Star Icon (spans two rows) */}
                  <Image src="/star.png" mt={1} boxSize="48px" alt="Points Icon" alignSelf="flex-start" />

                  {/* Current Points and Total Points */}
                  <Stack spacing={0}>
                    <Text fontSize="sm" mt={1} color={'gray.400'} fontWeight="medium">
                      {t("total_points")}
                    </Text>
                    <SkeletonText
                      noOfLines={1}
                      spacing="2"
                      skeletonHeight="6"
                      width="25px"
                      isLoaded={!isRefreshing}
                    >
                      <Text fontSize="xl" mt={-1} fontWeight="bold" color={pointBadgeTextColor}>
                        {/* {userData?.current_points ?? 0}  */}
                        {dateUtils.convertToBurmeseNumerals(userData?.current_points ?? 0, savedLanguage)}
                      </Text>
                    </SkeletonText>
                  </Stack>
                </Flex>

                {/* Refresh Button */}
                <IconButton
                  //padding="4px 6px"
                  borderRadius="20%"
                  bg={bgColorIcon}
                  transition="background-color 1.5s ease"
                  icon={isRefreshing ? <Spinner size="sm" /> : 
                    <svg viewBox="0 0 20 20" width="15" height="15" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path fill="#ffffff" d="M19.295346,12 C19.683732,11.997321 20,12.315434 20,12.7087815 L20,15.9132194 C20,16.3046684 19.6866632,16.6220005 19.3001428,16.6220005 C18.9136223,16.6220005 18.6002855,16.3046684 18.6002855,15.9132194 L18.6006646,14.7880072 C16.7783174,17.8441657 13.3981233,20 9.75558622,20 C5.34669464,20 1.65005079,17.2790232 0.0473577091,13.0847914 C-0.0921406706,12.7197255 0.0869918429,12.3092534 0.447461376,12.1679763 C0.80793091,12.0266992 1.21323498,12.2081158 1.35273336,12.5731817 C2.75210409,16.2353209 5.94083219,18.5824378 9.75558622,18.5824378 C13.1270432,18.5824378 16.2763668,16.4010153 17.7430824,13.4292559 L16.2715084,13.4386023 C15.884997,13.4412853 15.56952,13.1261356 15.566854,12.7346958 C15.5642216,12.343256 15.8754035,12.0237564 16.2619149,12.0210734 L19.295346,12 Z M10.2444138,0 C14.6533054,0 18.3499492,2.72097676 19.9526423,6.9152086 C20.0921407,7.28027447 19.9130082,7.69074656 19.5525386,7.83202368 C19.1920691,7.9733008 18.786765,7.79188418 18.6472666,7.4268183 C17.2478959,3.76467906 14.0591678,1.4175622 10.2444138,1.4175622 C6.87295684,1.4175622 3.72363319,3.59898468 2.25691759,6.57074409 L3.72849164,6.56139765 C4.11500304,6.5587147 4.43048002,6.87386439 4.43314598,7.26530419 C4.43577836,7.65674399 4.12459654,7.97624361 3.73808514,7.97892656 L0.704653993,8 C0.316268039,8.00267895 4.36983782e-13,7.68456603 4.36983782e-13,7.29121854 L4.36983782e-13,4.0867806 C4.36983782e-13,3.69533161 0.31333676,3.3779995 0.699857241,3.3779995 C1.08637772,3.3779995 1.39971448,3.69533161 1.39971448,4.0867806 L1.39933538,5.21199282 C3.22168264,2.1558343 6.60187665,0 10.2444138,0 Z"></path> </g></svg>
                  }
                  aria-label="Refresh Points"
                  onClick={refreshData}
                  isLoading={isRefreshing}
                  // colorScheme="teal"
                  variant="ghost"
                  position="absolute"
                  size="sm"
                  top="4"
                  right="3"
                  _hover={{
                    "& > svg": {
                      transform: "rotate(360deg)",
                      transition: "transform 0.8s ease-in-out",
                    },
                  }}
                  sx={{
                    "& > svg": {
                      transition: "transform 0.8s ease-in-out",
                    },
                  }}
                />
                {/* Reward Progress */}
                <RewardProgress textColor={rewardProgressTextColor} currentStamps={stamps} maxStamps="5" onRedeem={onOpen}/>

                {/* StampCard positioned at the bottom-right */}
                <Box position="absolute" bottom="58px" right="10px" onClick={handleFetchUserStampTransactions}>
                  <StampCard 
                    totalStamps={userData?.current_stamps} 
                    stampTransactions={stampData.earned}
                    usedStamps={stampData.used}
                    expiredStamps={stampData.expired}
                    isLoading={isUserStampTransactionLoading} 
                    />
                </Box>
              </Box>

              {/* Flip Card and Main Content Section */}
              <Flex position="relative" mt={11} left="0" right="0" justify="center">
                <FlipCard frontImage="/logo-reward.jpg" frontText="Reward Card" backImage={userData?.qrCode || ""} />
              </Flex>

              {/* Points and Reward Boxes */}
              <Flex p={1} gap={4} position="relative" mt={1}  justifyContent="center">
                {/* Total Points Box */}
                <Box
                  height="80px"
                  width="50%"
                  borderWidth="1px"
                  boxShadow="xl"
                  bg={bgColorDark}
                  borderRadius="lg"
                  textAlign="center"
                  position="relative"
                  top="0.5rem"
                  cursor="pointer"
                  onClick={() => navigate(`/point/${userInfo?._id}`)}
                  _hover={{ boxShadow: "2xl", transform: "scale(1.05)", transition: "0.2s" }}
                >
                  <Flex alignItems="center" justifyContent="center" pb={3}>
                    {/* Skeleton effect for Points Badge */}
                    {isRefreshing ? (
                      <Skeleton
                        height="24px"
                        width="24px"
                        position="absolute"
                        top="-2"
                        right="2"
                        borderRadius="full"
                      />
                    ) : userData?.current_points > 0 ? (
                      <Badge
                        variant="solid"
                        rounded="xl"
                        boxSize="6"
                        position="absolute"
                        top="-2"
                        right="2"
                        colorScheme="red"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {userData?.current_points}
                      </Badge>
                    ) : null}

                    <Icon as={FaStar} color={pointBadgeTextColor} pt="3" boxSize={9} />
                  </Flex>
                  <Text fontSize="sm" mt={-2} color={pointBadgeTextColor} fontWeight="medium">
                    {t('points')}
                  </Text>

                  {/* Chevron Right Icon */}
                  <Icon
                    as={FaChevronRight}
                    position="absolute"
                    right="3"
                    top="50%"
                    transform="translateY(-50%)"
                    color={pointBadgeTextColor}
                    boxSize={4}
                  />
                </Box>

                {/* Reward Box */}
                <Box
                  height="80px"
                  width="50%"
                  borderWidth="1px"
                  boxShadow="xl"
                  bg={bgColorDark}
                  borderRadius="lg"
                  textAlign="center"
                  position="relative"
                  top="0.5rem"
                  cursor="pointer"
                  onClick={() => navigate(`/reward/${userData?._id}`)}
                  _hover={{ boxShadow: "2xl", transform: "scale(1.05)", transition: "0.2s" }}
                >
                  <Flex alignItems="center" justifyContent="center" pb={3}>
                    {isRefreshing ? (
                      <Skeleton
                        height="24px"
                        width="24px"
                        position="absolute"
                        top="-2"
                        right="2"
                        borderRadius="full"
                      />
                    ) : rewards?.totalRedeemableLimited > 0 ? (
                      <Badge
                        variant="solid"
                        rounded="xl"
                        boxSize="6"
                        position="absolute"
                        top="-2"
                        right="2"
                        colorScheme="red"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {rewards?.totalRedeemableLimited}
                      </Badge>
                    ) : null}

                    <Icon as={FaGift} color={pointBadgeTextColor} pt="3" boxSize={9} />
                  </Flex>

                  <Text fontSize="sm" mt={-2} color={pointBadgeTextColor} fontWeight="medium">
                    {t('rewards')}
                  </Text>

                  {/* Chevron Right Icon */}
                  <Icon
                    as={FaChevronRight}
                    position="absolute"
                    right="3"
                    top="50%"
                    transform="translateY(-50%)"
                    color={pointBadgeTextColor}
                    boxSize={4}
                  />
                </Box>
              </Flex>
              <Flex p={1} gap={4} position="relative" mt={-3} justifyContent="center">
                <Box
                    width="100%"
                    borderWidth="1px"
                    boxShadow="xl"
                    bg={bgColorDark}
                    borderRadius="lg"
                    textAlign="center"
                    position="relative"
                    top="1.5rem"
                    cursor="pointer"
                    onClick={() => navigate('/shop-locations')}
                    _hover={{ boxShadow: "2xl", transform: "scale(1.05)", transition: "0.2s" }}
                  >
                    <Flex alignItems="center" mt={2} justifyContent="center" pb={3}>
                    <Box as="span" color={pointBadgeTextColor}>
                      <FontAwesomeIcon 
                        icon={faMapLocationDot} 
                        fontSize="24px" 
                        aria-label="Shop Locations"
                      />
                    </Box>
                      <Text fontSize="sm" pl={2} color={pointBadgeTextColor} fontWeight="medium">
                        {t('menu.items.shop_locations')}
                      </Text>
                      {/* Chevron Right Icon */}
                    <Icon
                      as={FaChevronRight}
                      position="absolute"
                      right="3"
                      top="50%"
                      transform="translateY(-50%)"
                      color={pointBadgeTextColor}
                      boxSize={4}
                    />
                    </Flex>
                </Box>
              </Flex>
            </Stack>

            {/* Stamp Reward Modal */}
            <StampReward isOpen={isOpen} 
            onClose={onClose} 
            refreshData={refreshData} 
            reward={stampRewardData} isLoading={isLoading} isError={isError} />
            
          </Flex>
        {/* User Header */}
        <UserHeader userInfo={userData} />
        </>
      ) : (
        <>
        <Hero />
        </>
      )}
    </>
  );
};

export default Dashboard;
